import axios, { CancelTokenSource } from 'axios';
import CheckBox from 'components/final-form/CheckBox';
import {
  DropdownOption,
  mapToDropdownOptionArray,
  stringArrayToDropdownOptionArray
} from 'components/final-form/dropdownUtils';
import { selectInput } from 'components/final-form/SelectInput';
import { textInput } from 'components/final-form/TextInput';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import React, { Component } from "react";
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Header } from 'semantic-ui-react';
import { getAllCountries } from 'services/countryServices';
import { editDoctor, saveDoctor } from 'services/doctorServices';
import { UpsertDoctorDto } from 'ts-types/api.types';
import { noop } from 'utils/functionUtils';
import { IndexSignature } from 'utils/praxTypes';
import { required } from 'utils/validatorUtils';

interface Props extends MessageSourceProps {
  toggleAddEditModal: () => void,
  onSave: () => void,
  onCancel: () => void,
  doctorId?: number,
  doctor?: UpsertDoctorDto
}

interface State {
  errorMessage?: string,
  billTexts: Array<DropdownOption>
  countries?: Array<DropdownOption>,
  cancelTokenSource: CancelTokenSource
}

class UpsertDoctorForm extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {getMessage} = this.props;
    const cancelTokenSource = axios.CancelToken.source();

    const billTextLabels: Array<string> = ["german", "french", "italian", "english"];
    const billTextKeys: Array<string> = ["de", "fr", "it", "en"];

    getAllCountries(cancelTokenSource)
        .then(response => {
          this.setState({
            countries: mapToDropdownOptionArray(response, "name", "shortname")
          })
        }).catch(noop);

    this.state = {
      billTexts: stringArrayToDropdownOptionArray(billTextLabels, getMessage, "billText", billTextKeys),
      cancelTokenSource
    }
  };

  componentWillUnmount(): void {

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  };

  initialDoctorValues = (): Partial<UpsertDoctorDto> => {

    const {countries} = this.state;

    return {
      active: true,
      referral: false,
      representing: false,
      countryId: countries ? countries[0].value : undefined,
      language: 'de'
    };
  };

  handleSubmit = async (values: Partial<UpsertDoctorDto>) => {
    const {doctorId, getMessage, onSave} = this.props;

    const handleError = () => {
      this.setState({
        errorMessage: getMessage("error.general")
      })
    };

    if (doctorId) {
      await editDoctor(doctorId, values)
          .then(onSave)
          .catch(handleError)
    } else {
      await saveDoctor(values)
          .then(onSave)
          .catch(handleError)
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  render(): React.ReactNode {

    const {doctor = this.initialDoctorValues()} = this.props;

    return (
        <FinalForm
            onSubmit={(values) => this.handleSubmit(values)}
            initialValues={doctor}
            subscription={{pristine: true, submitting: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({handleSubmit, submitting}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCancel} = this.props;
    const {errorMessage} = this.state;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              {errorMessage
              && (
                  <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                    {errorMessage}
                  </StyledErrorMessage>
              )
              }
              <PraxFormGrid>
                {this.renderBasicInformationSection()}
              </PraxFormGrid>
            </FormContent>

            <FormButtonsRow>
              <Button compact type="submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>

          </FormContainer>
        </form>
    );
  };

  renderStatusSection = (): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <PraxFormGrid>
          <Grid.Row>
            <Grid.Column>
              <Header as='h5'>
                {getMessage("status")}
              </Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={2}>
            </Grid.Column>
            <Grid.Column width={5} style={{paddingLeft: "41px"}}>
              <Field
                  name="representing"
                  component={CheckBox}
                  label={`${getMessage("representing")} ${getMessage("doctor")}`}
              />
            </Grid.Column>
            <Grid.Column width={9} style={{paddingLeft: "206px"}}>
              <Field
                  name="referral"
                  component={CheckBox}
                  label={`${getMessage("referral")} ${getMessage("doctor")}`}
              />
            </Grid.Column>
          </Grid.Row>
        </PraxFormGrid>
    );
  };

  renderActiveSection = (): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <Field
              name="active"
              component={CheckBox}
              label={`${getMessage("active")}`}
              toggle
          />
        </React.Fragment>
    );
  };

  renderBasicInformationSection = (): React.ReactNode => {

    const {countries, billTexts} = this.state;
    const {getMessage} = this.props;

    const validator = (value: string) => required(value);

    const selectMap: IndexSignature = {
      countryId: countries,
      language: billTexts
    };

    const addressField = [
      textInput({
        name: "salutation"
      }),
      textInput({
        name: "title"
      }),
      textInput({
        name: "name",
        validate: validator
      }),
      textInput({
        name: "firstName"
      }),
      textInput({
        name: "street"
      }),
      textInput({
        name: "zip"
      }),
      textInput({
        name: "city",
        validate: validator
      }),
      selectInput({
            name: "countryId",
            validate: validator
          },
          selectMap
      ),
      selectInput({
            name: "language",
            validate: validator
          },
          selectMap
      ),
      textInput({
        name: "representingCanton"
      })
    ];

    const additionalInfoField = [
      textInput({
        name: "other"
      }),
      textInput({
        name: "speciality"
      }),
      textInput({
        name: "position"
      }),
      textInput({
        name: "email"
      }),
      textInput({
        name: "telephone"
      }),
      textInput({
        name: "fax"
      }),
      textInput({
        name: "identification"
      }),
      textInput({
        name: "konkNumber"
      }),
      textInput({
        name: "eanNumber"
      }),
      textInput({
        name: "zsrNumber"
      })
    ];

    const mappedLeftRow = addressField.map((fieldProps, index) => {

      return (
          <Grid.Row key={index}>
            <Grid.Column width={5} verticalAlign="middle">
              {`${getMessage(fieldProps.name)}:`}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  {...fieldProps}
              />
            </Grid.Column>
          </Grid.Row>
      );
    });

    const mappedRightRow = additionalInfoField.map((fieldProps, index) => {

      return (
          <Grid.Row key={index}>
            <Grid.Column width={5} verticalAlign="middle">
              {`${getMessage((fieldProps.name))}:`}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  {...fieldProps}
              />
            </Grid.Column>
          </Grid.Row>
      );
    });

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column width={8}>
              <Header as='h5'>
                {getMessage("address")}
              </Header>
            </Grid.Column>
            <Grid.Column width={5} verticalAlign="middle">
              <Header as='h5'>
                {getMessage("additional.info")}
              </Header>
            </Grid.Column>
            <Grid.Column width={3} textAlign="right">
              {this.renderActiveSection()}
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={8}>
              <PraxFormGrid>
                {mappedLeftRow}
              </PraxFormGrid>
            </Grid.Column>
            <Grid.Column width={8}>
              <PraxFormGrid>
                {mappedRightRow}
              </PraxFormGrid>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row style={{paddingTop: "2rem"}}>
            <Grid.Column>
              {this.renderStatusSection()}
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    );
  };
}

export default withMessages(UpsertDoctorForm);
